<!--
# -----------------------------------------------------------------------------
# Component: CreateReceipt.vue
# Author: antonio.ramirez@pst.sg
# Created Date: 14/May/2019
# Comments: Page for Create Receipt Functionality
# -----------------------------------------------------------------------------
-->
<template>
  <page>
    <v-container fluid grid-list-lg>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card>
            <v-toolbar dense color="grey lighten-3" flat>
              <v-icon>mdi-plus</v-icon>
              <v-toolbar-title>Create New Receipt</v-toolbar-title>
            </v-toolbar>
            <v-tabs v-model="tabsNav">
              <v-tab @click.native="doValidateMandatory(0)">Mandatory</v-tab>
              <v-tab @click.native="doValidateMandatory(1)">Optional</v-tab>
              <v-tab @click.native="doValidateMandatory(2)">Payment Method</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tabsNav" touchless>
              <v-tab-item transition="fade-transition" reverse-transition="fade-transition" key="mandatory" eager>
                <form data-vv-scope="receiptsMandatoryForm">
                  <v-flex xs12>
                    <v-card>
                      <v-card-text>
                        <v-container fluid grid-list-lg>
                          <v-layout row wrap>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="branches"
                                v-model="receiptsForm.branch"
                                :label="receiptsFormLabel.branch"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.branch"
                                :data-vv-as="receiptsFormLabel.branch"
                                v-validate="'required'"
                                :error-messages="errors.collect('receiptsForm.branch')"
                                clearable
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md2 xs12>
                              <v-checkbox
                                color="primary"
                                :label="receiptsFormLabel.docAE"
                                v-model="receiptsForm.docAE"
                                @change="checkDocAE($event)"
                              ></v-checkbox>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-text-field
                                v-model="receiptsForm.documentNo"
                                :disabled="isDisabledDocNo"
                                @blur="checkDocNo"
                                v-validate="docNoValidation"
                                :label="receiptsFormLabel.documentNo"
                                :error-messages="errors.collect('receiptsForm.documentNo')"
                                data-vv-name="receiptsForm.documentNo"
                                :data-vv-as="receiptsFormLabel.documentNo"
                                :maxlength="8"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md6 xs12>
                              <cal-picker
                                v-model="receiptsForm.transactionDateDisp"
                                :label="receiptsFormLabel.transactionDateDisp"
                                :data-vv-as="receiptsFormLabel.transactionDateDisp"
                                v-validate="doValidateTranDate"
                                data-vv-name="receiptsForm.transactionDateDisp"
                                :error-messages="errors.collect('receiptsForm.transactionDateDisp')"
                                @change="checkCurrRate"
                                :loading="loading"
                              ></cal-picker>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="accounts"
                                v-model="receiptsForm.receivedFrom"
                                item-text="label"
                                item-value="value"
                                :label="receiptsFormLabel.receivedFrom"
                                :error-messages="errors.collect('receiptsForm.receivedFrom')"
                                data-vv-name="receiptsForm.receivedFrom"
                                :data-vv-as="receiptsFormLabel.receivedFrom"
                                v-validate="'required'"
                                @change="getAcctInfo($event)"
                                clearable
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showAcctNoHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="banks"
                                v-model="receiptsForm.depositAcct"
                                item-text="label"
                                item-value="value"
                                :label="receiptsFormLabel.depositAcct"
                                :error-messages="errors.collect('receiptsForm.depositAcct')"
                                data-vv-name="receiptsForm.depositAcct"
                                :data-vv-as="receiptsFormLabel.depositAcct"
                                v-validate="'required'"
                                @change="getBankInfo($event)"
                                clearable
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showBankNoHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-autocomplete>
                            </v-flex>
                            <v-flex md1 xs12>
                              <v-autocomplete
                                :items="funds"
                                v-model="receiptsForm.fund"
                                :label="receiptsFormLabel.fund"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.fund"
                                :data-vv-as="receiptsFormLabel.fund"
                                :error-messages="errors.collect('receiptsForm.fund')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md5 xs12>
                              <v-autocomplete
                                :items="acctnosuffixes"
                                v-model="receiptsForm.accountNoSuffixDesc"
                                item-text="label"
                                item-value="value"
                                :label="receiptsFormLabel.accountNoSuffixDesc"
                                :error-messages="errors.collect('receiptsForm.accountNoSuffixDesc')"
                                data-vv-name="receiptsForm.accountNoSuffixDesc"
                                :data-vv-as="receiptsFormLabel.accountNoSuffixDesc"
                                v-validate="'required|entry|max:60'"
                                @change="getAcctNoSfxInfo($event)"
                                clearable
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showAcctSufHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="paymentmethods"
                                v-model="receiptsForm.paymentMethod"
                                :label="receiptsFormLabel.paymentMethod"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.paymentMethod"
                                :data-vv-as="receiptsFormLabel.paymentMethod"
                                v-validate="'required'"
                                :error-messages="errors.collect('receiptsForm.paymentMethod')"
                                @change="getPaymentMethod($event)"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md2 xs12>
                              <v-autocomplete
                                :items="gstIOOptions"
                                v-model="receiptsForm.gstIO"
                                :label="receiptsFormLabel.gstIO"
                                item-text="label"
                                item-value="value"
                                data-vv-name="gstIO"
                                :data-vv-as="receiptsFormLabel.gstIO"
                                :error-messages="errors.collect('gstIO')"
                                @change="changeGstIO($event)"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md2 xs12>
                              <v-autocomplete
                                :items="gstStatuses"
                                v-model="receiptsForm.gstStatus"
                                :label="receiptsFormLabel.gstStatus"
                                item-text="label"
                                item-value="value"
                                data-vv-name="gstIO"
                                :data-vv-as="receiptsFormLabel.gstStatus"
                                :error-messages="errors.collect('gstStatus')"
                                @change="changeGstRate($event)"
                                :disabled="isBlankGSTIO"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md2 xs12>
                              <v-text-field
                                v-model="receiptsForm.gstRate"
                                :disabled="true"
                                :label="receiptsFormLabel.gstRate"
                                :error-messages="errors.collect('gstRate')"
                                data-vv-name="gstRate"
                                :data-vv-as="receiptsFormLabel.gstRate"
                                v-validate="'entry|max:60'"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-autocomplete
                                :items="amountcurrencies"
                                v-model="receiptsForm.amountCurr"
                                :label="receiptsFormLabel.amountCurr"
                                item-text="label"
                                item-value="value"
                                :error-messages="errors.collect('receiptsForm.amountCurr')"
                                data-vv-name="receiptsForm.amountCurr"
                                :data-vv-as="receiptsFormLabel.amountCurr"
                                v-validate="'required'"
                                clearable
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showCurrHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-autocomplete>
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.amount"
                                :label="receiptsFormLabel.amount"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                :error-messages="errors.collect('receiptsForm.amount')"
                                data-vv-name="receiptsForm.amount"
                                :data-vv-as="receiptsFormLabel.amount"
                                v-validate="'required|mask_decimal:2|mask_max_value:9999999999999.99|mask_min_value:0.01'"
                                @blur="calculateAmount()"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md3 xs12>
                              <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.gstAmountCurr"
                                :label="receiptsFormLabel.gstAmountCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.gstAmountCurr"
                                :data-vv-as="receiptsFormLabel.gstAmountCurr"
                                :error-messages="errors.collect('receiptsForm.gstAmountCurr')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md1 xs12>
                              <v-checkbox
                                color="primary"
                                v-model="receiptsForm.gstAmountAE"
                                :disabled="isGstAmtAE"
                                @change="calculateAmount()"
                              ></v-checkbox>
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.gstAmount"
                                :disabled="isGstAmount"
                                :label="receiptsFormLabel.gstAmount"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                :error-messages="errors.collect('receiptsForm.gstAmount')"
                                data-vv-name="receiptsForm.gstAmount"
                                :data-vv-as="receiptsFormLabel.gstAmount"
                                v-validate="'mask_decimal:2|mask_max_value:9999999999999.99|mask_min_value:-9999999999999.99'"
                                @blur="calculateAmount()"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.netAmountCurr"
                                :label="receiptsFormLabel.netAmountCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.netAmountCurr"
                                :data-vv-as="receiptsFormLabel.netAmountCurr"
                                :error-messages="errors.collect('receiptsForm.netAmountCurr')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.netAmount"
                                :disabled="true"
                                :label="receiptsFormLabel.netAmount"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                :error-messages="errors.collect('receiptsForm.netAmount')"
                                data-vv-name="receiptsForm.netAmount"
                                :data-vv-as="receiptsFormLabel.netAmount"
                                v-validate="'mask_decimal:2|mask_max_value:9999999999999.99|mask_min_value:-9999999999999.99'"
                                @blur="calculateAmount()"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md3 xs12>
                              <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.bankAmountCurr"
                                :label="receiptsFormLabel.bankAmountCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.bankAmountCurr"
                                :data-vv-as="receiptsFormLabel.bankAmountCurr"
                                :error-messages="errors.collect('receiptsForm.bankAmountCurr')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md1 xs12>
                              <v-checkbox color="primary" v-model="receiptsForm.bankAmountAE" @change="calculateAmount()"></v-checkbox>
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.bankAmount"
                                :disabled="isBankAmount"
                                :label="receiptsFormLabel.bankAmount"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                :error-messages="errors.collect('receiptsForm.bankAmount')"
                                data-vv-name="receiptsForm.bankAmount"
                                :data-vv-as="receiptsFormLabel.bankAmount"
                                v-validate="'mask_decimal:2|mask_max_value:9999999999999.99|mask_min_value:-9999999999999.99'"
                                @blur="calculateAmount()"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.bankChargesCurr"
                                :label="receiptsFormLabel.bankChargesCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.bankChargesCurr"
                                :data-vv-as="receiptsFormLabel.bankChargesCurr"
                                :error-messages="errors.collect('receiptsForm.bankChargesCurr')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.bankCharges"
                                :label="receiptsFormLabel.bankCharges"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                :error-messages="errors.collect('receiptsForm.bankCharges')"
                                data-vv-name="receiptsForm.bankCharges"
                                :data-vv-as="receiptsFormLabel.bankCharges"
                                v-validate="'mask_decimal:2|mask_max_value:9999999999999.99'"
                                @blur="calculateAmount()"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.bankTotalAmountCurr"
                                :label="receiptsFormLabel.bankTotalAmountCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.bankTotalAmountCurr"
                                :data-vv-as="receiptsFormLabel.bankTotalAmountCurr"
                                :error-messages="errors.collect('receiptsForm.bankTotalAmountCurr')"
                                disabled
                              />
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.bankTotalAmount"
                                :disabled="true"
                                :label="receiptsFormLabel.bankTotalAmount"
                                :error-messages="errors.collect('bankTotalAmount')"
                                data-vv-name="bankTotalAmount"
                                :data-vv-as="receiptsFormLabel.bankTotalAmount"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                v-validate="'entry|max:60'"
                              ></v-text-field>
                            </v-flex>
                            <v-autocomplete
                                :items="currencies"
                                v-model="receiptsForm.bankTotalLocalCurr"
                                :label="receiptsFormLabel.bankTotalLocalCurr"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.bankTotalLocalCurr"
                                :data-vv-as="receiptsFormLabel.bankTotalLocalCurr"
                                :error-messages="errors.collect('receiptsForm.bankTotalLocalCurr')"
                                disabled
                              />
                            <v-flex md1 xs12>
                              <v-checkbox
                                color="primary"
                                v-model="receiptsForm.bankTotalLocalAE"
                                :disabled="isEqual"
                                @change="calculateAmount()"
                              ></v-checkbox>
                            </v-flex>
                            <v-flex md8 xs12>
                              <v-text-field
                                v-model="receiptsForm.bankTotalLocal"
                                :disabled="isBankTotalLocal"
                                :label="receiptsFormLabel.bankTotalLocal"
                                :error-messages="errors.collect('bankTotalLocal')"
                                data-vv-name="bankTotalLocal"
                                :data-vv-as="receiptsFormLabel.bankTotalLocal"
                                v-num-mask="{ negative: true, intScale: 13, decScale: 2 }"
                                v-validate="'entry|max:60'"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md6 xs12 sm6 lg6 v-if="isNew">
                              <v-subheader class="queryLabel">{{ receiptsFormLabel.createdBy }}</v-subheader>
                              <div class="queryValue">{{ createdName + ' on ' + nowDT }}</div>
                            </v-flex>
                            <v-flex md6 xs12 sm6 lg6 v-else>
                              <v-subheader class="queryLabel">{{ receiptsFormLabel.createdBy }}</v-subheader>
                              <div class="queryValue">{{ receiptsForm.createdBy + ' on ' + receiptsForm.createdDate }}</div>
                            </v-flex>
                            <v-flex md6 xs12 sm6 lg6 v-if="isNew">
                              <v-subheader class="queryLabel">{{ receiptsFormLabel.modifiedBy }}</v-subheader>
                              <div class="queryValue">{{ createdName + ' on ' + nowDT }}</div>
                            </v-flex>
                            <v-flex md6 xs12 sm6 lg6 v-else>
                              <v-subheader class="queryLabel">{{ receiptsFormLabel.modifiedBy }}</v-subheader>
                              <div class="queryValue">{{ receiptsForm.modifiedBy + ' on ' + receiptsForm.modifiedDate }}</div>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                </form>
              </v-tab-item>
              <v-tab-item transition="fade-transition" reverse-transition="fade-transition" key="optional" eager>
                <form data-vv-scope="receiptsOptionalForm">
                  <v-flex xs12>
                    <v-card>
                      <v-card-text>
                        <v-container fluid grid-list-lg>
                          <v-layout row wrap>
                            <v-flex xs12>
                              <div class="header-padding body-2">Document Details</div>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="printDocOptions"
                                v-model="receiptsForm.printDocument"
                                :label="receiptsFormLabel.printDocument"
                                item-text="label"
                                item-value="value"
                                data-vv-name="printDocument"
                                :data-vv-as="receiptsFormLabel.printDocument"
                                :error-messages="errors.collect('printDocument')"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="addressToOptions"
                                v-model="receiptsForm.addressTo"
                                :label="receiptsFormLabel.addressTo"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.addressTo"
                                :data-vv-as="receiptsFormLabel.addressTo"
                                :error-messages="errors.collect('receiptsForm.addressTo')"
                                @change="promptRemoveNameAddr = true"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-text-field
                                v-model="receiptsForm.policyNo"
                                :label="receiptsFormLabel.policyNo"
                                :error-messages="errors.collect('policyNo')"
                                data-vv-name="policyNo"
                                :data-vv-as="receiptsFormLabel.policyNo"
                                :maxlength="16"
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showAcctPolicyHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.companyName"
                                :label="receiptsFormLabel.companyName"
                                :error-messages="errors.collect('receiptsOptionalForm.companyName')"
                                data-vv-name="companyName"
                                :data-vv-as="receiptsFormLabel.companyName"
                                v-validate="'max:100|sanction:' + sanctionName1Error"
                                @change="valSanction(receiptsForm.companyName, '', 'sanctionName1Error')"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.docAddress1"
                                :label="receiptsFormLabel.docAddress1"
                                :error-messages="errors.collect('docAddress1')"
                                data-vv-name="docAddress1"
                                :data-vv-as="receiptsFormLabel.docAddress1"
                                :maxlength="60"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.docAddress2"
                                :label="receiptsFormLabel.docAddress2"
                                :error-messages="errors.collect('docAddress2')"
                                data-vv-name="docAddress2"
                                :data-vv-as="receiptsFormLabel.docAddress2"
                                :maxlength="60"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.docAddress3"
                                :label="receiptsFormLabel.docAddress3"
                                :error-messages="errors.collect('docAddress3')"
                                data-vv-name="docAddress3"
                                :data-vv-as="receiptsFormLabel.docAddress3"
                                :maxlength="60"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.docAddress4"
                                :label="receiptsFormLabel.docAddress4"
                                :error-messages="errors.collect('docAddress4')"
                                data-vv-name="docAddress4"
                                :data-vv-as="receiptsFormLabel.docAddress4"
                                :maxlength="60"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-text-field
                                v-model="receiptsForm.postalCode"
                                :label="receiptsFormLabel.postalCode"
                                :error-messages="errors.collect('postalCode')"
                                data-vv-name="postalCode"
                                :data-vv-as="receiptsFormLabel.postalCode"
                                :maxlength="8"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-textarea
                                v-model="receiptsForm.details"
                                :label="receiptsFormLabel.details"
                                no-resize
                                rows="3"
                                :error-messages="errors.collect('details')"
                                data-vv-name="details"
                                :data-vv-as="receiptsFormLabel.details"
                                :maxlength="120"
                              ></v-textarea>
                            </v-flex>
                            <v-flex md12 xs12>
                              <v-textarea
                                v-model="receiptsForm.statementRef"
                                :label="receiptsFormLabel.statementRef"
                                no-resize
                                rows="3"
                                :error-messages="errors.collect('statementRef')"
                                data-vv-name="statementRef"
                                :data-vv-as="receiptsFormLabel.statementRef"
                                :maxlength="200"
                              ></v-textarea>
                            </v-flex>
                            <v-flex xs12>
                              <div class="header-padding body-2">Other Details</div>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="departments"
                                v-model="receiptsForm.department"
                                :label="receiptsFormLabel.department"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.department"
                                :data-vv-as="receiptsFormLabel.department"
                                :error-messages="errors.collect('receiptsForm.department')"
                                v-validate="departmentValidate"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-text-field
                                v-model="receiptsForm.insuredName"
                                :label="receiptsFormLabel.insuredName"
                                :error-messages="errors.collect('insuredName')"
                                data-vv-name="insuredName"
                                :data-vv-as="receiptsFormLabel.insuredName"
                                :maxlength="60"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-text-field
                                v-model="receiptsForm.claimNo"
                                :label="receiptsFormLabel.claimNo"
                                :error-messages="errors.collect('claimNo')"
                                data-vv-name="claimNo"
                                :data-vv-as="receiptsFormLabel.claimNo"
                                :maxlength="12"
                              >
                                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                  <v-icon color="info" @click="showClmHelpList = true" v-text="'mdi-magnify'"></v-icon>
                                </v-slide-x-reverse-transition>
                              </v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-text-field
                                v-model="receiptsForm.gstRegNo"
                                :label="receiptsFormLabel.gstRegNo"
                                :error-messages="errors.collect('gstRegNo')"
                                data-vv-name="gstRegNo"
                                :data-vv-as="receiptsFormLabel.gstRegNo"
                                :maxlength="20"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md4 xs12>
                              <v-text-field
                                v-model="receiptsForm.taxInvoiceNo"
                                :label="receiptsFormLabel.taxInvoiceNo"
                                :error-messages="errors.collect('taxInvoiceNo')"
                                data-vv-name="taxInvoiceNo"
                                :data-vv-as="receiptsFormLabel.taxInvoiceNo"
                                :maxlength="30"
                              ></v-text-field>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                </form>
              </v-tab-item>
              <v-tab-item transition="fade-transition" reverse-transition="fade-transition" key="paymentmethod" eager>
                <form data-vv-scope="receiptsPaymentForm">
                  <v-flex xs12>
                    <v-card>
                      <v-card-text>
                        <v-container fluid grid-list-lg>
                          <v-layout row wrap>
                            <v-flex md6 xs12>
                              <v-autocomplete
                                :items="payerbanks"
                                :disabled="isPayBank"
                                v-model="receiptsForm.payerBank"
                                :label="receiptsFormLabel.payerBank"
                                item-text="label"
                                item-value="value"
                                data-vv-name="receiptsForm.payerBank"
                                :data-vv-as="receiptsFormLabel.payerBank"
                                :error-messages="errors.collect('receiptsForm.payerBank')"
                                v-validate="payerBankValidate"
                              ></v-autocomplete>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-text-field
                                v-model="receiptsForm.payersChequeNo"
                                :disabled="isChequeNo"
                                :label="receiptsFormLabel.payersChequeNo"
                                :error-messages="errors.collect('receiptsForm.payersChequeNo')"
                                data-vv-name="receiptsForm.payersChequeNo"
                                :data-vv-as="receiptsFormLabel.payersChequeNo"
                                :maxlength="10"
                                v-validate="payChqNoValidate"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-text-field
                                v-model="receiptsForm.drawerName"
                                :disabled="isDrawerName"
                                :label="receiptsFormLabel.drawerName"
                                :error-messages="errors.collect('receiptsForm.drawerName')"
                                data-vv-name="receiptsForm.drawerName"
                                :data-vv-as="receiptsFormLabel.drawerName"
                                :maxlength="50"
                                v-validate="drawerNameValidate"
                              ></v-text-field>
                            </v-flex>
                            <v-flex md6 xs12>
                              <v-text-field
                                v-model="receiptsForm.creditCardNo"
                                :disabled="isCreditCardNo"
                                :label="receiptsFormLabel.creditCardNo"
                                :error-messages="errors.collect('receiptsForm.creditCardNo')"
                                data-vv-name="receiptsForm.creditCardNo"
                                :data-vv-as="receiptsFormLabel.creditCardNo"
                                :maxlength="20"
                                v-validate="creditCardNoValidate"
                              ></v-text-field>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                </form>
              </v-tab-item>
            </v-tabs-items>
            <v-card-actions>
              <v-btn color="error" dark @click.prevent="doCancel">
                {{ actionLabel.cancel }}
                <v-icon dark right>mdi-exit-to-app</v-icon>
              </v-btn>
              <v-btn color="secondary" dark @click="showMatchLater = true">
                {{ actionLabel.matchLater }}
                <v-icon dark right>mdi-content-copy</v-icon>
              </v-btn>
              <v-btn color="info" dark @click.prevent="doSaveReceipt('CREATE_MATCH_NOW')" :loading="submitting">
                {{ actionLabel.matchNow }}
                <v-icon dark right>mdi-content-copy</v-icon>
              </v-btn>
              <v-btn color="info" dark @click.prevent="doSaveReceipt('CREATE_SAVE_REVIEW')" :loading="submitting">
                {{ actionLabel.saveAndReview }}
                <v-icon dark right>mdi-content-save</v-icon>
              </v-btn>
              <v-btn color="primary" dark @click.prevent="doSaveReceipt('CREATE_SAVE_POST')" :loading="submitting">
                {{ actionLabel.saveAndPost }}
                <v-icon dark right>mdi-arrow-right-bold-box</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <!--Added: 31/May  Show Currency Help List -->
        <v-dialog v-model="showCurrHelpList" width="1280px">
          <currency-help-list @close="showCurrHelpList = false" @assign-currency-info="assignCurrencyInfo"></currency-help-list>
        </v-dialog>
        <!--Added: 31/May  Show Acct No Help List -->
        <v-dialog v-model="showAcctNoHelpList" width="1280px">
          <acct-no-help-list
            @close="showAcctNoHelpList = false"
            @assign-acct-info="assignAcctInfo"
            :boolRefresh="showAcctNoHelpList"
          ></acct-no-help-list>
        </v-dialog>
        <!--Added: 31/May  Show Bank Help List -->
        <v-dialog v-model="showBankNoHelpList" width="1280px">
          <bank-help-list
            @close="showBankNoHelpList = false"
            @assign-bank-info="assignBankInfo"
            :boolRefresh="showBankNoHelpList"
            :objData="bankHelpListScreen"
          ></bank-help-list>
        </v-dialog>
        <!--Added: 12/May  Show Acct Suffix Help List -->
        <v-dialog v-model="showAcctSufHelpList" width="1280px">
          <acct-suf-help-list @close="showAcctSufHelpList = false" @assign-acct-suf-info="assignAcctSufInfo"></acct-suf-help-list>
        </v-dialog>
        <!--Added: 11/May  Show Acct Policy Help List -->
        <v-dialog v-model="showAcctPolicyHelpList" width="1280px">
          <acct-policy-help-list
            :accountNo="receiptsForm.accountNo"
            :boolPolRefresh="showAcctPolicyHelpList"
            @close="showAcctPolicyHelpList = false"
            @assign-policy-info="assignPolicyInfo"
          ></acct-policy-help-list>
        </v-dialog>
        <!--Added: 11/May  Show Claim Help List -->
        <v-dialog v-model="showClmHelpList" width="1280px">
          <claim-help-list
            @close="showClmHelpList = false"
            @assign-claim-no="assignClaimNo"
            :boolRefresh="showClmHelpList"
            :accountNo="receiptsForm.accountNo"
          ></claim-help-list>
        </v-dialog>
        <!--Need to make it common and global for reusability-->
        <v-dialog v-model="processingFlag" persistent width="300">
          <v-card color="primary" dark>
            <v-card-text>
              Processing...
              <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
        <v-dialog v-model="loadingFlag" persistent width="300">
          <v-card color="primary" dark>
            <v-card-text>
              Loading...
              <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
        <warning v-model="promptMsg" :label="errMsg" :showAcceptBtn="isWarning" @ok="acceptInterWarn = true"/>
        <v-dialog v-model="showErrorDialog" width="800px">
          <v-card>
            <v-toolbar dense color="grey lighten-3" flat>
              <v-icon>mdi-information</v-icon>
              <v-toolbar-title>System Information</v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <div v-if="messages.length > 0" class="pb-3">
                <span class="info-dialog-msg-header">Oops something went wrong...</span>
                <v-spacer class="mt-2"></v-spacer>
                <ul class="showErrorMsgs">
                  <li v-for="(error, index) in messages" :key="index">
                    {{ error.message }}
                  </li>
                </ul>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!--LYT Added: 2020-08-27  Sanction Partial Matched Names List -->
        <v-dialog v-model="showSanctionDialog" width="1280px">
          <sanction-partial-match-list :sanctionItems="sanctionPartialMatchItems" @close="showSanctionDialog = false"></sanction-partial-match-list>
        </v-dialog>

        <v-dialog v-model="showMatchLater" width="680px">
          <match-later
            :multiFundSfxCurr="miscacctparams.multiFundSfxCurr"
            :tranType="receiptsForm.transactionType"
            :curr="receiptsForm.amountCurr"
            :acctNo="receiptsForm.accountNo"
            :fund="receiptsForm.fund"
            :acctNoSfx="receiptsForm.accountNoSuffix"
            :polNo="receiptsForm.policyNo"
            :boolRefresh="showMatchLater"
            @close="showMatchLater = false"
            @assign-match-later-info="assignMatchLaterInfo"
          ></match-later>
        </v-dialog>
        <prompt-leave v-if="showPromptLeave" title="Receipt" v-model="promptLeave" @exit="doExit" @saveAndExit="doSaveAndExit"></prompt-leave>

        <confirm v-model="promptConfirmation" @ok="doProceed" @cancel="processingFlag = false">
          <p>Cheque No. is existing in other receipt, do you want to proceed?</p>
          <div class="queryValue">
            Bank No. : {{ this.receiptsForm.bankNo }}<br />
            Cheque No. : {{ this.receiptsForm.payersChequeNo }}<br />
          </div>
        </confirm>
        <confirm v-model="promptRemoveNameAddr" @ok="doRemoveNameAddr">
          {{ promptRemoveNameAddrMsg }}
        </confirm>
      </v-layout>
    </v-container>
  </page>
</template>

<script>
import routeMixin from '@/mixins/route'
import MessageMixin from '@/mixins/message'
import { receiptsViewMixin } from '@/mixins/accounting/receipts/receiptsViewMixin'
import { receiptsAjaxMixin } from '@/mixins/accounting/receipts/receiptsAjaxMixin'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import CurrencyHelpList from '@/components/core/common/dialog/CurrencyHelpList'
import AcctNoHelpList from '@/components/core/common/dialog/AcctNoHelpList'
import BankHelpList from '@/components/core/common/dialog/BankHelpList'
import AcctSufHelpList from '@/components/core/common/dialog/AcctSufHelpList'
import AcctPolicyHelpList from '@/components/core/common/dialog/AcctPolicyHelpList'
import ClaimHelpList from '@/components/core/common/dialog/ClaimHelpList'
import { removeFormatNumber } from '@/utils/number'
import { promptOnExitMixin } from '@/mixins/common/promptOnExitMixin'
import PromptLeave from '@/components/core/common/prompt/PromptLeave'
import { formatPrice } from '@/utils/index'
import { parseISO } from 'date-fns'
import { formatDisplayDate, formatDisplayDateTime2, removeTime } from '@/utils/date'
import SanctionPartialMatchList from '@/components/core/common/dialog/SanctionPartialMatchList'
import MatchLater from '../dialog/MatchLater'
import isAfter from 'date-fns/isAfter'

export default {
  data() {
    return {
      receiptTabs: ['Mandatory', 'Optional', 'Payment Method'],
      tabsNav: 0,
      loading: false,
      loadingFlag: false,
      processingFlag: false,
      showErrorDialog: false,
      submitting: false,
      showPromptLeave: true,
      branches: [],
      currencies: [],
      payerbanks: [],
      paymentmethods: [],
      departments: [],
      cashbooks: [],
      banks: [],
      miscacctparams: [],
      accounts: [],
      amountcurrencies: [],
      acctnosuffixes: [],
      funds: [],
      acctnosuffixdata: {},
      amountcurrencydata: {},
      paymentmethod: {},
      accountdata: {},
      bankdata: {},
      trandaterange: {},
      showCurrHelpList: false,
      showAcctNoHelpList: false,
      showBankNoHelpList: false,
      showAcctSufHelpList: false,
      showAcctPolicyHelpList: false,
      showClmHelpList: false,
      actionLabel: {
        cancel: 'Cancel',
        saveAndReview: 'Save & Review',
        saveAndPost: 'Save & Post',
        matchNow: 'Match Now',
        matchLater: 'Match Later',
        matchRBC: 'Match RBC'
      },
      validStatus: {
        mandatory: true,
        optional: true,
        paymentmethod: true
      },
      receiptErrors: [],
      gstStatuses: [],
      gstrate: '0.00',
      promptConfirmation: false,
      isExistChequeNo: false,
      actionName: null,
      showMatchLater: false,
      promptRemoveNameAddr: false,
      promptRemoveNameAddrMsg: 'Do you want to remove Name, Address(1-4), Postal Code?',
      baseCurrency: ''
    }
  },
  watch: {
    displayErrorDialog: {
      handler: function(messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    },
    payChqNoValidate: {
      handler: function(isMandatory) {
        if (isMandatory) {
          this.receiptsFormLabel.payersChequeNo = 'Cheque No*'
        } else {
          this.receiptsFormLabel.payersChequeNo = 'Cheque No'
        }
      }
    },
    payerBankValidate: {
      handler: function(isMandatory) {
        if (isMandatory) {
          this.receiptsFormLabel.payerBank = 'Payer Bank*'
        } else {
          this.receiptsFormLabel.payerBank = 'Payer Bank'
        }
      }
    },
    drawerNameValidate: {
      handler: function(isMandatory) {
        if (isMandatory) {
          this.receiptsFormLabel.drawerName = 'Drawer Name*'
        } else {
          this.receiptsFormLabel.drawerName = 'Drawer Name'
        }
      }
    },
    creditCardNoValidate: {
      handler: function(isMandatory) {
        if (isMandatory) {
          this.receiptsFormLabel.creditCardNo = 'Credit Card No*'
        } else {
          this.receiptsFormLabel.creditCardNo = 'Credit Card No'
        }
      }
    },
    departmentValidate: {
      handler: function(isMandatory) {
        if (isMandatory) {
          this.receiptsFormLabel.department = 'Department*'
        } else {
          this.receiptsFormLabel.department = 'Department'
        }
      }
    },
    'tDateRange.effDate': function() {
      if (this.tDateRange.effDate !== '') {
        this.loading = true
        this.getCommonData('gstrate', this.receiptsForm.gstStatus, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp)).then(result => {
          if (result) {
            this.loading = false
          }
        })
      }
    },
    'receiptsForm.amountCurr': function() {
      if (this.receiptsForm.amountCurr && this.receiptsForm.transactionDateDisp) {
        this.getCommonData('currencyrate', this.receiptsForm.amountCurr, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
      }
    }
  },
  computed: {
    doValidateTranDate() {
      const minDate = this.tDateRange.effDate
      const today = this.tDateRange.expDate
      return 'required|valMinTranDate:' + minDate + '|valMaxTranDate:' + today
    },
    displayErrorDialog() {
      return this.messages
    },
    isGstAmtAE: function() {
      return this.receiptsForm.gstIO === 'B'
    },
    isBlankGSTIO: function() {
      return this.receiptsForm.gstIO === 'B'
    },
    isDisabledDocNo: function() {
      return this.receiptsForm.docAE
    },
    isGstAmount: function() {
      return this.receiptsForm.gstAmountAE
    },
    isBankAmount: function() {
      return this.receiptsForm.bankAmountAE
    },
    isBankTotalLocal: function() {
      return this.receiptsForm.bankTotalLocalAE
    },
    isCreditCardNo: function() {
      return this.paymentmethod.crcdNo !== 'M' && this.paymentmethod.crcdNo !== 'O'
    },
    isDrawerName: function() {
      return this.paymentmethod.drawName !== 'M' && this.paymentmethod.drawName !== 'O'
    },
    isChequeNo: function() {
      return this.paymentmethod.payChqNo !== 'M' && this.paymentmethod.payChqNo !== 'O'
    },
    isPayBank: function() {
      return this.paymentmethod.payBank !== 'M' && this.paymentmethod.payBank !== 'O'
    },
    isCashBook: function() {
      return this.miscacctparams.cashBkMand === null || this.miscacctparams.cashBkMand === false
    },
    payChqNoValidate() {
      if (this.paymentmethod.payChqNo === 'M') {
        return 'required'
      } else {
        return ''
      }
    },
    payerBankValidate() {
      if (this.paymentmethod.payBank === 'M') {
        return 'required'
      } else {
        return ''
      }
    },
    drawerNameValidate() {
      if (this.paymentmethod.drawName === 'M') {
        return 'required'
      } else {
        return ''
      }
    },
    creditCardNoValidate() {
      if (this.paymentmethod.crcdNo === 'M') {
        return 'required'
      } else {
        return ''
      }
    },
    departmentValidate() {
      if (this.miscacctparams.deptMand) {
        return 'required'
      } else {
        return ''
      }
    },
    tDateRange() {
      return this.trandaterange
    },
    docNoValidation() {
      if (this.receiptsForm.docAE === false && (this.receiptsForm.documentNo === null || this.receiptsForm.documentNo === '')) {
        return 'required'
      } else {
        return 'entry|documentNo:' + this.receiptsForm.isExistDocNo
      }
    },
    apiURI() {
      return this.$store.state.receipt.menu
    },
    isEqual: function() {
      if (
        this.receiptsForm.amountCurr === this.receiptsForm.bankAmountCurr &&
        this.receiptsForm.bankAmountCurr === this.receiptsForm.bankTotalLocalCurr
      ) {
        return true
      } else {
        return false
      }
    },
    isNew() {
      return !(this.receiptData.tranMode === 'Q' || this.receiptData.tranMode === 'R')
    }
  },
  methods: {
    changeGstIO(gstIO) {
      this.receiptsForm.gstIO = gstIO
      if (this.receiptsForm.gstIO === 'B') {
        this.receiptsForm.gstStatus = 'B'
        this.receiptsForm.gstAmount = this.defaultAmount()
        this.receiptsForm.gstRate = this.defaultAmount()
      } else {
        this.receiptsForm.gstStatus = 'M'
        this.getCommonData('gstrate', this.receiptsForm.gstStatus, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
      }
      this.calculateAmount()
    },
    calculateAmount() {
      let bankAmt = 0.0
      let bankTotAmt = 0.0
      if (this.receiptsForm.depositAcct !== null) {
        if (this.receiptsForm.gstAmountAE) {
          // Only if GST Rate is not equal to zero (0), then GST Amount = Amount * GSTRate/100
          this.receiptsForm.gstAmount = formatPrice((removeFormatNumber(this.receiptsForm.amount) * this.receiptsForm.gstRate) / 100)
        }
        /*
          If GSTIO = Blank then Net Amount = Amount.
          If GSTIO = I then Net Amount = Amount – GST Amount
          If GSTIO = O then Net Amount = Amount + GST Amount
        */
        if (this.receiptsForm.gstIO === 'B') {
          this.receiptsForm.netAmount = formatPrice(Number(removeFormatNumber(this.receiptsForm.amount)))
        }
        if (this.receiptsForm.gstIO === 'I') {
          this.receiptsForm.netAmount = formatPrice(
            removeFormatNumber(this.receiptsForm.amount) - Number(removeFormatNumber(this.receiptsForm.gstAmount))
          )
        }
        if (this.receiptsForm.gstIO === 'O') {
          this.receiptsForm.netAmount = formatPrice(
            Number(removeFormatNumber(this.receiptsForm.amount)) + Number(removeFormatNumber(this.receiptsForm.gstAmount))
          )
        }
        if (this.receiptsForm.bankAmountAE) {
          // Bank Amount = (Net Amount * Net Amount Currency Rate) / Bank Amount Currency Rate
          this.receiptsForm.bankAmount = formatPrice(
            (removeFormatNumber(this.receiptsForm.netAmount) * this.receiptsForm.amountCurrRate) / this.receiptsForm.bankAmountCurrRate
          )
        }
        // Bank Amount - Bank Charges
        this.receiptsForm.bankTotalAmount = formatPrice(
          removeFormatNumber(this.receiptsForm.bankAmount) - removeFormatNumber(this.receiptsForm.bankCharges)
        )
        if (this.receiptsForm.bankTotalLocalAE) {
          //  ONLY IF bankTotalLocalAE is checked. Bank Amount * Bank Total Amount currency rate
          if (this.receiptsForm.bankAmountAE) {
            bankAmt =
              (Number(removeFormatNumber(this.receiptsForm.netAmount)) * Number(removeFormatNumber(this.receiptsForm.amountCurrRate))) /
              Number(removeFormatNumber(this.receiptsForm.bankAmountCurrRate))
          } else {
            bankAmt = removeFormatNumber(this.receiptsForm.bankAmount)
          }
          bankTotAmt = Number(bankAmt) - Number(removeFormatNumber(this.receiptsForm.bankCharges))
          bankTotAmt = bankTotAmt * Number(this.receiptsForm.bankAmountCurrRate)
          this.receiptsForm.bankTotalLocal = formatPrice(bankTotAmt)
        }
      }
    },
    currencyChange() {
      this.receiptsForm.gstAmountCurr = this.receiptsForm.amountCurr
      this.receiptsForm.netAmountCurr = this.receiptsForm.amountCurr
    },
    checkDocAE(event) {
      if (event) {
        this.receiptsForm.documentNo = null
        this.resetMessages()
        this.resetValidator()
      }
    },
    changeGstRate(gstStatus) {
      this.getCommonData('gstrate', gstStatus, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
    },
    assignAcctSufInfo(data) {
      this.receiptsForm.accountNoSuffixDesc = data.acctNoSfx
      this.receiptsForm.accountNoSuffix = data.acctNoSfx
      this.showAcctSufHelpList = false
    },
    assignCurrencyInfo(data) {
      this.receiptsForm.amountCurr = data.curr
      this.receiptsForm.gstAmountCurr = data.curr
      this.receiptsForm.netAmountCurr = data.curr
      this.receiptsForm.amountCurrRate = data.currRate
      this.showCurrHelpList = false
      this.calculateAmount()
    },
    assignAcctInfo(data) {
      this.receiptsForm.accountNo = data.inter
      this.receiptsForm.receivedFrom = data.inter
      if (this.receiptsForm.addressTo === 'A') {
        this.receiptsForm.gstRegNo = data.gstRegNo
        this.receiptsForm.docAddress1 = data.address1
        this.receiptsForm.docAddress2 = data.address2
        this.receiptsForm.docAddress3 = data.address3
        this.receiptsForm.docAddress4 = data.address4
        this.receiptsForm.companyName = data.name
        this.receiptsForm.postalCode = data.postalCode
      }
      this.showAcctNoHelpList = false
    },
    assignBankInfo(data) {
      this.receiptsForm.amountCurr = data.bankAcctCurrency
      this.receiptsForm.gstAmountCurr = data.bankAcctCurrency
      this.receiptsForm.netAmountCurr = data.bankAcctCurrency
      this.receiptsForm.bankAmountCurr = data.bankAcctCurrency
      this.receiptsForm.bankChargesCurr = data.bankAcctCurrency
      this.receiptsForm.bankTotalAmountCurr = data.bankAcctCurrency
      this.receiptsForm.fund = data.bankFund
      this.receiptsForm.accountNoSuffix = data.bankAcctNoSfx
      this.receiptsForm.paymentMethod = data.bankPaymentMethod
      this.receiptsForm.depositAcct = data.defPayBank
      this.receiptsForm.bankNo = data.defPayBank
      this.receiptsForm.bankAmountCurrRate = data.bankCurrRate
      this.receiptsForm.amountCurrRate = data.bankCurrRate
      this.showBankNoHelpList = false
      this.calculateAmount()
    },
    assignPolicyInfo(data) {
      this.receiptsForm.policyNo = data.policyNo + '-' + data.renCnt + '-' + data.endtCnt
      this.receiptsForm.insuredName = data.insuredName
      this.receiptsForm.insdRefNo = data.insdRefNo
      if (this.receiptsForm.addressTo === 'I') {
        this.receiptsForm.companyName = data.fullName
        this.receiptsForm.docAddress1 = data.address1
        this.receiptsForm.docAddress2 = data.address2
        this.receiptsForm.docAddress3 = data.address3
        this.receiptsForm.docAddress4 = data.address4
        this.receiptsForm.postalCode = data.postalCode
      }
      this.showAcctPolicyHelpList = false
    },
    assignClaimNo(claimNo) {
      this.receiptsForm.claimNo = claimNo
      this.showClmHelpList = false
    },
    assignGSTRate(data) {
      this.receiptsForm.gstRate = formatPrice(data)
      this.calculateAmount()
    },
    doRemoveNameAddr() {
      this.receiptsForm.companyName = ''
      this.receiptsForm.docAddress1 = ''
      this.receiptsForm.docAddress2 = ''
      this.receiptsForm.docAddress3 = ''
      this.receiptsForm.docAddress4 = ''
      this.receiptsForm.postalCode = ''
    },
    assignMatchLaterInfo(data) {
      this.receiptsForm.bmAcctNo = data.bmAcctNo
      this.receiptsForm.bmAcctNoSfx = data.bmAcctNoSfx
      this.receiptsForm.bmBrn = data.bmBrn
      this.receiptsForm.bmFund = data.bmFund
      this.receiptsForm.bmTranType = data.bmTranType
      this.receiptsForm.bmDocNo = data.bmDocNo
      this.receiptsForm.vehRegNo = data.vehRegNo
      this.receiptsForm.cnNo = data.coverNote
      this.receiptsForm.policyNo = data.polNo
      this.receiptsForm.insuredName = data.insuredName
      this.receiptsForm.insdRefNo = data.insdRefNo
      if (this.receiptsForm.addressTo === 'I') {
        this.receiptsForm.companyName = data.fullName
        this.receiptsForm.docAddress1 = data.address1
        this.receiptsForm.docAddress2 = data.address2
        this.receiptsForm.docAddress3 = data.address3
        this.receiptsForm.docAddress4 = data.address4
        this.receiptsForm.postalCode = data.postalCode
      }
      this.showMatchLater = false
      this.doSaveReceipt('CREATE_MATCH_LATER')
    },
    defaultAmount() {
      return (0.0).toFixed(2)
    },
    async doInit() {
      this.loadingFlag = true
      await Promise.all([this.$store.dispatch('commonsInit')])
      await Promise.all([this.$store.dispatch('reportsInit')])
      await Promise.all([this.$store.dispatch('acctReportsInit')])
      this.getDefaultBankNo(this.$store.state.token.userBranch, this.receiptsForm, 'receipt').then(result => {
        if (result) {
          this.getPaymentMethod(this.receiptsForm.paymentMethod)
        }
      })
      this.receiptsForm.transactionDateDisp = new Date()
      this.receiptsForm.transactionType = 'Y'
      this.receiptsForm.docAE = true
      this.receiptsForm.documentNoDisabled = true
      this.receiptsForm.gstAmountAE = true
      this.receiptsForm.bankAmountAE = true
      this.receiptsForm.bankTotalLocalAE = true
      this.receiptsForm.gstIO = 'B'
      this.receiptsForm.gstStatus = 'N'
      this.receiptsForm.addressTo = 'A'
      this.receiptsForm.branch = this.$store.state.token.userBranch
      this.bankHelpListScreen = 'screen_1'
      this.getAcctRptMenuLink()
      this.getCommonData('trandaterange', '003') // 003 = For Receipts
      this.getCommonData('miscacctparams').then(() => {
        this.receiptsForm.printDocument = this.miscacctparams.defIlnrcpt
      })
      this.getBaseCurr(this.receiptsForm)
      this.fetchBranch()
      this.fetchFund()
      this.fetchAcctNoSfx()
      this.fetchGstStatus()
      this.fetchCurrency()
      this.fetchAmountCurrency()
      this.fetchPaymentMethod()
      this.fetchDepartment()
      this.fetchBank()
      this.fetchCashBook()
      this.fetchPayerBank()
      this.fetchAccount().then(() => {
        this.loadingFlag = false
      })
    },
    async getAcctInfo(acctNo) {
      this.getCommonData('accountdata', acctNo)
    },
    async getBankInfo(bankNo) {
      this.getCommonData('bankdata', bankNo, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
    },
    assignCurrRate(currRate) {
      this.receiptsForm.amountCurrRate = currRate
      this.receiptsForm.gstAmountCurr = this.receiptsForm.amountCurr
      this.receiptsForm.netAmountCurr = this.receiptsForm.amountCurr
      this.calculateAmount()
    },
    assignBankCurrRate(data) {
      this.receiptsForm.bankAmountCurrRate = data.bankCurrRate
      this.calculateAmount()
    },
    checkCurrRate() {
      if (this.receiptsForm.amountCurr && this.receiptsForm.transactionDateDisp) {
        this.getCommonData('currencyrate', this.receiptsForm.amountCurr, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
        this.getCommonData('bankcurrencyrate', this.receiptsForm.depositAcct, formatDisplayDateTime2(this.receiptsForm.transactionDateDisp))
      }
    },
    async getAcctNoSfxInfo(acctNoSfx) {
      this.getCommonData('acctnosuffixdata', acctNoSfx)
    },
    doCancel() {
      this.$router.push({ name: 'receipts' }).catch(() => {})
    },
    checkChequeNo() {
      if (this.receiptsForm.payersChequeNo !== null && this.receiptsForm.payersChequeNo !== '') {
        this.getAcctMasterRcptChequeNo(this.receiptsForm).then(result => {
          if (result) {
            if (this.isExistChequeNo === 'true') {
              this.promptConfirmation = true
            } else {
              this.doSave()
            }
          }
        })
      } else {
        this.doSave()
      }
    },
    checkDocNo() {
      this.isDocNoExist(this.receiptsForm)
    },
    doProceed() {
      this.promptConfirmation = false
      this.doSave()
    },
    doSaveReceipt(actionName) {
      this.actionName = actionName
      this.processingFlag = true
      this.checkChequeNo()
    },
    doSave() {
      let isMandatoryFormValid = false
      let isOptionalFormValid = false
      this.processingFlag = true
      this.resetMessages()
      this.$validator.validateAll('receiptsMandatoryForm').then(result => {
        if (result) {
          this.ValidateTranDateRange(this.receiptsForm.transactionDateDisp, '003').then(result => {
            if (result) {
              this.validateIntermediary(this.receiptsForm.transactionDateDisp, this.receiptsForm.receivedFrom).then(() => {
                if (this.promptMsg === false || this.acceptInterWarn === true) {
                  this.validStatus.mandatory = true
                  isMandatoryFormValid = true
                } else {
                  this.setValidationValues(0)
                  isMandatoryFormValid = false
                  this.processingFlag = false
                }
                if (isMandatoryFormValid === true) {
                  this.$validator.validateAll('receiptsOptionalForm').then(result => {
                    if (result) {
                      this.validStatus.optional = true
                      isOptionalFormValid = true
                    } else {
                      this.setValidationValues(1)
                      isOptionalFormValid = false
                      this.processingFlag = false
                    }
                    if (isOptionalFormValid === true) {
                      this.validatePaymentForm(this.actionName)
                    }
                  })
                }
              })
            } else {
              this.setValidationValues(0)
              isMandatoryFormValid = false
              this.processingFlag = false
            }
          })
        } else {
          this.setValidationValues(0)
          isMandatoryFormValid = false
          this.processingFlag = false
        }
      })
    },
    validatePaymentForm(actionName) {
      // Check if Payment Fields are Mandatory
      if (
        this.paymentmethod.payChqNo === 'M' ||
        this.paymentmethod.drawName === 'M' ||
        this.paymentmethod.crcdNo === 'M' ||
        this.paymentmethod.payBank === 'M'
      ) {
        this.$validator.validateAll('receiptsPaymentForm').then(result => {
          if (result) {
            this.validStatus.paymentmethod = true
            this.processSave(actionName)
          } else {
            this.setValidationValues(2)
          }
        })
      } else {
        this.processSave(actionName)
      }
    },
    processSave(actionName) {
      this.receiptsForm.actionName = actionName
      this.showPromptLeave = false
      this.performSave()
    },
    setValidationValues(tabId) {
      this.processingFlag = false
      if (tabId === 0) this.validStatus.mandatory = false
      if (tabId === 1) this.validStatus.optional = false
      if (tabId === 2) this.validStatus.paymentmethod = false
      this.tabsNav = tabId
    },
    doValidateMandatory(tabId) {
      this.resetMessages()
      if (tabId === 1) {
        this.$validator.validateAll('receiptsMandatoryForm').then(result => {
          if (result) {
            this.ValidateTranDateRange(this.receiptsForm.transactionDateDisp, '003').then(result => {
              if (result) {
                this.validateIntermediary(this.receiptsForm.transactionDateDisp, this.receiptsForm.receivedFrom).then(() => {
                  if (this.promptMsg === false || this.acceptInterWarn === true) {
                    this.validStatus.mandatory = true
                    this.tabsNav = tabId
                  } else {
                    this.validStatus.mandatory = false
                    this.tabsNav = 0
                  }
                })
              } else {
                this.validStatus.mandatory = false
                this.tabsNav = 0
              }
            })
          } else {
            this.validStatus.mandatory = false
            this.tabsNav = 0
            this.promptFieldCheck()
          }
        })
      } else if (tabId === 2) {
        this.$validator.validateAll('receiptsMandatoryForm').then(result => {
          if (result) {
            this.ValidateTranDateRange(this.receiptsForm.transactionDateDisp, '003').then(result => {
              if (result) {
                this.validateIntermediary(this.receiptsForm.transactionDateDisp, this.receiptsForm.receivedFrom).then(() => {
                  if (this.promptMsg === false || this.acceptInterWarn === true) {
                    this.$validator.validateAll('receiptsOptionalForm').then(result => {
                      if (result) {
                        this.validStatus.optional = true
                        this.tabsNav = tabId
                      } else {
                        this.validStatus.optional = false
                        this.tabsNav = 1
                      }
                    })
                  } else {
                    this.validStatus.optional = false
                    this.tabsNav = 0
                  }
                })
              } else {
                this.validStatus.mandatory = false
                this.tabsNav = 0
              }
            })
          } else {
            this.validStatus.mandatory = false
            this.tabsNav = 0
            this.promptFieldCheck()
          }
        })
      } else if (tabId === 3) {
        // Placeholder
      }
    },
    parseDateISO(value) {
      if (value) {
        return parseISO(value)
      }
    }
  },
  created() {
    this.$validator.extend('documentNo', {
      getMessage(field, args) {
        return 'This document no. is exist.'
      },
      validate(value, args) {
        if (args === void 0) args = []
        var isdocNoExist = args[0]
        if (isdocNoExist === 'true') {
          return false
        } else {
          return true
        }
      }
    })
    this.$validator.extend('valMaxTranDate', {
      getMessage(field, args) {
        return 'This date cannot be greater than the date range ' + formatDisplayDate(args[0])
      },
      validate(value, args) {
        let maxDate = args[0]
        if (value) {
          return !isAfter(removeTime(value), removeTime(maxDate))
        } else return true
      }
    })
    this.$validator.extend('valMinTranDate', {
      getMessage(field, args) {
        return 'This date cannot be lesser than the date range ' + formatDisplayDate(args[0])
      },
      validate(value, args) {
        let minDate = args[0]
        if (value) {
          return !isAfter(removeTime(minDate), removeTime(value))
        } else return true
      }
    })
  },
  mounted() {
    this.doInit()
  },
  components: {
    AcctNoHelpList,
    CurrencyHelpList,
    BankHelpList,
    AcctSufHelpList,
    AcctPolicyHelpList,
    ClaimHelpList,
    PromptLeave,
    SanctionPartialMatchList,
    MatchLater
  },
  mixins: [routeMixin, MessageMixin, receiptsViewMixin, receiptsAjaxMixin, commonAjaxMixin, promptOnExitMixin]
}
</script>
<style lang="scss">
@import '../../../../assets/scss/accounting.scss';
</style>
